import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ClipboardCheck, Plus, Pencil, Trash2, Search, CheckCircle2, AlertTriangle, AlertCircle, History, BarChart3, Activity, Calendar } from "lucide-react";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { useTranslation } from "../contexts/I18nContext";

const STATI = [
  { value: "Buono", label: "Buono", color: "bg-green-100 text-green-800 border-green-300", icon: CheckCircle2, score: 3, hex: "#16a34a" },
  { value: "Da sistemare", label: "Da sistemare", color: "bg-amber-100 text-amber-800 border-amber-300", icon: AlertTriangle, score: 2, hex: "#d97706" },
  { value: "Urgente", label: "Urgente", color: "bg-red-100 text-red-800 border-red-300", icon: AlertCircle, score: 1, hex: "#dc2626" },
];

const STATO_MAP = Object.fromEntries(STATI.map(s => [s.value, s]));

export default function ControlliAppartamentiPage() {
  const prefix = getApiPrefix();
  const { t } = useTranslation();
  const [controlli, setControlli] = useState([]);
  const [appartamenti, setAppartamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterAppartamento, setFilterAppartamento] = useState("");
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [form, setForm] = useState({ appartamento_id: "", data_visita: "", stato: "Buono", note: "" });
  const [activeTab, setActiveTab] = useState("elenco");

  // Format helpers
  const formatDate = (v) => {
    if (!v) return '-';
    const s = String(v).split('T')[0];
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
  };
  const daysBetween = (d1, d2) => {
    if (!d1 || !d2) return null;
    const a = new Date(d1);
    const b = new Date(d2);
    if (isNaN(a) || isNaN(b)) return null;
    return Math.round((b - a) / (1000 * 60 * 60 * 24));
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ctrlRes, appRes] = await Promise.all([
        api.get(`${prefix}/controlli-appartamenti`).catch(() => ({ data: [] })),
        api.get(`${prefix}/appartamenti`).catch(() => ({ data: [] })),
      ]);
      setControlli(ctrlRes.data || []);
      setAppartamenti(appRes.data || []);
    } catch (err) {
      handleApiError(err, t("controls.errorLoad"));
    } finally {
      setLoading(false);
    }
  }, [prefix, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const text = filter.trim().toLowerCase();
    return controlli.filter(c => {
      if (filterStato && c.stato !== filterStato) return false;
      if (filterAppartamento && c.appartamento_id !== filterAppartamento) return false;
      if (text) {
        const hay = [
          c.appartamento_nome, c.indirizzo, c.citta, c.stato, c.note, c.data_visita
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(text)) return false;
      }
      return true;
    });
  }, [controlli, filter, filterStato, filterAppartamento]);

  // Conteggi per stato
  const counters = useMemo(() => {
    const out = { Buono: 0, "Da sistemare": 0, Urgente: 0, totale: controlli.length };
    controlli.forEach(c => { if (out[c.stato] !== undefined) out[c.stato] += 1; });
    return out;
  }, [controlli]);

  // Statistiche per appartamento (per la tab "Statistiche")
  const statsPerAppartamento = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const scoreByStato = Object.fromEntries(STATI.map(s => [s.value, s.score]));
    // Includi solo gli appartamenti che hanno almeno un controllo
    const byApp = {};
    controlli.forEach(c => {
      if (!c.appartamento_id) return;
      (byApp[c.appartamento_id] ||= []).push(c);
    });

    const rows = Object.entries(byApp).map(([appId, list]) => {
      // Ordina per data_visita ASC per calcolare intervalli
      const sorted = [...list].sort((a, b) => (a.data_visita || "").localeCompare(b.data_visita || ""));
      const counts = { Buono: 0, "Da sistemare": 0, Urgente: 0 };
      let scoreSum = 0;
      sorted.forEach(c => {
        if (counts[c.stato] !== undefined) counts[c.stato] += 1;
        scoreSum += scoreByStato[c.stato] || 0;
      });
      const ultima = sorted[sorted.length - 1];
      const prima = sorted[0];
      const giorniDallUltima = daysBetween(ultima.data_visita, today);
      // Frequenza media (giorni fra visite) = (data_ultima - data_prima) / (N - 1)
      const intervalloTotale = sorted.length >= 2 ? daysBetween(prima.data_visita, ultima.data_visita) : null;
      const frequenzaMedia = (sorted.length >= 2 && intervalloTotale != null && intervalloTotale > 0)
        ? Math.round(intervalloTotale / (sorted.length - 1))
        : null;
      const scoreMedio = sorted.length > 0 ? scoreSum / sorted.length : 0;
      // Stato medio = quello dello score più vicino (1=Urgente, 2=Da sistemare, 3=Buono)
      const statoMedio = scoreMedio >= 2.5 ? "Buono" : scoreMedio >= 1.5 ? "Da sistemare" : "Urgente";
      const app = appartamenti.find(a => a.id === appId);
      return {
        appartamento_id: appId,
        nome: app?.nome || ultima.appartamento_nome || appId,
        indirizzo: app?.indirizzo || ultima.indirizzo,
        citta: app?.citta || ultima.citta,
        nVisite: sorted.length,
        ultima_visita: ultima.data_visita,
        ultimo_stato: ultima.stato,
        giorni_dall_ultima: giorniDallUltima,
        prima_visita: prima.data_visita,
        frequenza_media_giorni: frequenzaMedia,
        score_medio: scoreMedio,
        stato_medio: statoMedio,
        counts,
        pct_buono: sorted.length > 0 ? Math.round((counts["Buono"] / sorted.length) * 100) : 0,
        pct_sistemare: sorted.length > 0 ? Math.round((counts["Da sistemare"] / sorted.length) * 100) : 0,
        pct_urgente: sorted.length > 0 ? Math.round((counts["Urgente"] / sorted.length) * 100) : 0,
      };
    });

    // Includi anche appartamenti senza controlli (utile per evidenziarli)
    appartamenti.forEach(a => {
      if (!byApp[a.id]) {
        rows.push({
          appartamento_id: a.id,
          nome: a.nome,
          indirizzo: a.indirizzo,
          citta: a.citta,
          nVisite: 0,
          ultima_visita: null,
          ultimo_stato: null,
          giorni_dall_ultima: null,
          prima_visita: null,
          frequenza_media_giorni: null,
          score_medio: 0,
          stato_medio: null,
          counts: { Buono: 0, "Da sistemare": 0, Urgente: 0 },
          pct_buono: 0,
          pct_sistemare: 0,
          pct_urgente: 0,
        });
      }
    });

    // Ordina: appartamenti con almeno una visita prima (per giorni_dall_ultima DESC),
    // poi quelli senza visite alla fine.
    rows.sort((a, b) => {
      if (a.nVisite === 0 && b.nVisite > 0) return 1;
      if (a.nVisite > 0 && b.nVisite === 0) return -1;
      if (a.giorni_dall_ultima == null && b.giorni_dall_ultima == null) return 0;
      if (a.giorni_dall_ultima == null) return 1;
      if (b.giorni_dall_ultima == null) return -1;
      return b.giorni_dall_ultima - a.giorni_dall_ultima;
    });

    return rows;
  }, [controlli, appartamenti]);

  // Statistiche globali per la testa della tab Statistiche
  const statsGlobali = useMemo(() => {
    const totApp = appartamenti.length;
    const conControlli = statsPerAppartamento.filter(r => r.nVisite > 0).length;
    const senzaControlli = totApp - conControlli;
    // Media frequenza fra appartamenti che hanno almeno 2 visite
    const freqList = statsPerAppartamento
      .map(r => r.frequenza_media_giorni)
      .filter(v => v != null && v > 0);
    const frequenzaMediaGlobale = freqList.length
      ? Math.round(freqList.reduce((a, b) => a + b, 0) / freqList.length)
      : null;
    // Appartamenti con ultimo stato Urgente
    const urgentiAttuali = statsPerAppartamento.filter(r => r.ultimo_stato === "Urgente").length;
    return { totApp, conControlli, senzaControlli, frequenzaMediaGlobale, urgentiAttuali };
  }, [appartamenti, statsPerAppartamento]);

  // Storico per appartamento selezionato nel dialog (escluso quello in modifica)
  const storicoAppartamento = useMemo(() => {
    if (!form.appartamento_id) return [];
    return controlli
      .filter(c => c.appartamento_id === form.appartamento_id && c.id !== dialog.editing)
      .sort((a, b) => (b.data_visita || "").localeCompare(a.data_visita || ""));
  }, [form.appartamento_id, controlli, dialog.editing]);

  const openNew = () => {
    setForm({
      appartamento_id: "",
      data_visita: new Date().toISOString().split("T")[0],
      stato: "Buono",
      note: "",
    });
    setDialog({ open: true, editing: null });
  };

  const openEdit = (c) => {
    setForm({
      appartamento_id: c.appartamento_id,
      data_visita: c.data_visita || "",
      stato: c.stato,
      note: c.note || "",
    });
    setDialog({ open: true, editing: c.id });
  };

  const handleSave = async () => {
    if (!form.appartamento_id || !form.data_visita || !form.stato) {
      handleApiError({ message: t("controls.requiredFields") });
      return;
    }
    try {
      if (dialog.editing) {
        await api.put(`${prefix}/controlli-appartamenti/${dialog.editing}`, form);
        showSuccess(t("controls.controlUpdated"));
      } else {
        await api.post(`${prefix}/controlli-appartamenti`, form);
        showSuccess(t("controls.controlCreated"));
      }
      setDialog({ open: false, editing: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("controls.errorSave"));
    }
  };

  const handleDelete = async (id) => {
    if (!confirm(t("controls.confirmDelete"))) return;
    try {
      await api.delete(`${prefix}/controlli-appartamenti/${id}`);
      showSuccess(t("controls.controlDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("controls.errorDelete"));
    }
  };

  const renderStatoBadge = (stato) => {
    const s = STATO_MAP[stato];
    if (!s) return <Badge variant="outline">{stato}</Badge>;
    const Icon = s.icon;
    return (
      <Badge variant="outline" className={`${s.color} gap-1`}>
        <Icon className="h-3 w-3" /> {s.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-blue-600" />
            {t("controls.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("controls.registerVisitResult")}
          </p>
        </div>
        <Button onClick={openNew} data-testid="btn-nuovo-controllo">
          <Plus className="h-4 w-4 mr-2" /> {t("controls.newControl")}
        </Button>
      </div>

      {/* Statistiche stati */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground">{t("controls.totalControls")}</p>
            <p className="text-2xl font-bold" data-testid="counter-totale">{counters.totale}</p>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50/40">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> {t("controls.good")}</p>
            <p className="text-2xl font-bold text-green-700" data-testid="counter-buono">{counters["Buono"]}</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> {t("controls.toFix")}</p>
            <p className="text-2xl font-bold text-amber-700" data-testid="counter-sistemare">{counters["Da sistemare"]}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/40">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5 text-red-600" /> {t("controls.urgent")}</p>
            <p className="text-2xl font-bold text-red-700" data-testid="counter-urgente">{counters["Urgente"]}</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="elenco" data-testid="tab-elenco"><ClipboardCheck className="h-4 w-4 mr-1.5" /> {t("controls.list")}</TabsTrigger>
          <TabsTrigger value="statistiche" data-testid="tab-statistiche"><BarChart3 className="h-4 w-4 mr-1.5" /> {t("controls.statistics")}</TabsTrigger>
        </TabsList>

        <TabsContent value="elenco" className="mt-4">
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base">{t("controls.list")} ({filtered.length}{filter || filterStato || filterAppartamento ? ` / ${controlli.length}` : ""})</CardTitle>
          <CardDescription>{t("controls.orderedByDate")}</CardDescription>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("controls.searchPlaceholder")}
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="pl-9"
                data-testid="filter-text"
              />
            </div>
            <Select value={filterStato || "all"} onValueChange={v => setFilterStato(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="filter-stato"><SelectValue placeholder={t("controls.allStatuses")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("controls.allStatuses")}</SelectItem>
                {STATI.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <SearchableSelect
              options={[{ value: "__all__", label: t("controls.allApartments") }, ...appartamenti.map(a => ({ value: a.id, label: a.nome }))]}
              value={filterAppartamento || "__all__"}
              onValueChange={v => setFilterAppartamento(v === "__all__" ? "" : v)}
              placeholder={t("controls.filterByApartment")}
              searchPlaceholder={t("dialogs.searchApartment")}
              testId="filter-appartamento"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-muted-foreground">{t("common.loading")}</p>
          ) : filtered.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              {controlli.length === 0
                ? `${t("controls.noControlsRegistered")} "${t("controls.newControl")}".`
                : t("controls.noControlsMatchFilters")}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-2">{t("controls.visitDate")}</th>
                    <th className="py-2 px-2">{t("apartments.title").replace(/i$/, "o")}</th>
                    <th className="py-2 px-2">{t("controls.address")}</th>
                    <th className="py-2 px-2">{t("controls.status")}</th>
                    <th className="py-2 px-2">{t("common.notes")}</th>
                    <th className="py-2 px-2 text-right">{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <tr key={c.id} className="border-b hover:bg-muted/50" data-testid={`ctrl-row-${c.id}`}>
                      <td className="py-2 px-2 font-mono whitespace-nowrap">{formatDate(c.data_visita)}</td>
                      <td className="py-2 px-2 font-medium">{c.appartamento_nome || "—"}</td>
                      <td className="py-2 px-2 text-muted-foreground text-xs">
                        {[c.indirizzo, c.citta].filter(Boolean).join(", ") || "—"}
                      </td>
                      <td className="py-2 px-2">{renderStatoBadge(c.stato)}</td>
                      <td className="py-2 px-2 text-xs max-w-md truncate" title={c.note}>{c.note || "—"}</td>
                      <td className="py-2 px-2">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(c.id)} data-testid={`delete-${c.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="statistiche" className="mt-4 space-y-4">
          {/* KPI globali */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Appartamenti totali</p>
                <p className="text-2xl font-bold" data-testid="stat-tot-app">{statsGlobali.totApp}</p>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50/40">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Con controlli</p>
                <p className="text-2xl font-bold text-green-700">{statsGlobali.conControlli}</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/40">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Mai controllati</p>
                <p className="text-2xl font-bold text-amber-700" data-testid="stat-no-control">{statsGlobali.senzaControlli}</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/40">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5 text-red-600" /> Urgenti attuali</p>
                <p className="text-2xl font-bold text-red-700" data-testid="stat-urgenti">{statsGlobali.urgentiAttuali}</p>
                <p className="text-[11px] text-muted-foreground">{t("controls.lastStatus")} &quot;{t("controls.urgent")}&quot;</p>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50/40">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3.5 w-3.5 text-blue-600" /> Frequenza media</p>
                <p className="text-2xl font-bold text-blue-700">
                  {statsGlobali.frequenzaMediaGlobale != null ? `${statsGlobali.frequenzaMediaGlobale} gg` : '—'}
                </p>
                <p className="text-[11px] text-muted-foreground">Giorni medi fra visite</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-600" /> Dettaglio per appartamento
              </CardTitle>
              <CardDescription>
                {t("controls.orderedByDate")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {statsPerAppartamento.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nessun dato disponibile.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-2 px-2">{t('apartments.title')}</th>
                        <th className="py-2 px-2 text-right">N° Visite</th>
                        <th className="py-2 px-2">Prima visita</th>
                        <th className="py-2 px-2">Ultima visita</th>
                        <th className="py-2 px-2 text-right">Giorni fa</th>
                        <th className="py-2 px-2 text-right">Frequenza media</th>
                        <th className="py-2 px-2">Ultimo stato</th>
                        <th className="py-2 px-2">Stato medio</th>
                        <th className="py-2 px-2">Distribuzione stati</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statsPerAppartamento.map(r => (
                        <tr key={r.appartamento_id} className="border-b hover:bg-muted/50" data-testid={`stats-row-${r.appartamento_id}`}>
                          <td className="py-2 px-2">
                            <div className="font-medium">{r.nome}</div>
                            <div className="text-[11px] text-muted-foreground">{[r.indirizzo, r.citta].filter(Boolean).join(", ") || "—"}</div>
                          </td>
                          <td className="py-2 px-2 text-right font-mono">{r.nVisite}</td>
                          <td className="py-2 px-2 font-mono whitespace-nowrap">{formatDate(r.prima_visita)}</td>
                          <td className="py-2 px-2 font-mono whitespace-nowrap">{formatDate(r.ultima_visita)}</td>
                          <td className={`py-2 px-2 text-right font-mono ${r.giorni_dall_ultima != null && r.giorni_dall_ultima > 365 ? 'text-red-600 font-semibold' : r.giorni_dall_ultima != null && r.giorni_dall_ultima > 180 ? 'text-amber-600' : ''}`}>
                            {r.giorni_dall_ultima != null ? `${r.giorni_dall_ultima} gg` : '—'}
                          </td>
                          <td className="py-2 px-2 text-right font-mono">{r.frequenza_media_giorni != null ? `${r.frequenza_media_giorni} gg` : '—'}</td>
                          <td className="py-2 px-2">{r.ultimo_stato ? renderStatoBadge(r.ultimo_stato) : <span className="text-xs text-muted-foreground">—</span>}</td>
                          <td className="py-2 px-2">
                            {r.stato_medio ? (
                              <div className="flex items-center gap-2">
                                {renderStatoBadge(r.stato_medio)}
                                <span className="text-[11px] text-muted-foreground font-mono">({r.score_medio.toFixed(2)}/3)</span>
                              </div>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </td>
                          <td className="py-2 px-2 min-w-[200px]">
                            {r.nVisite > 0 ? (
                              <div className="space-y-1">
                                <div className="flex h-2 rounded overflow-hidden bg-slate-100" title={`Buono ${r.pct_buono}% · Da sistemare ${r.pct_sistemare}% · Urgente ${r.pct_urgente}%`}>
                                  {r.pct_buono > 0 && <div className="bg-green-500" style={{ width: `${r.pct_buono}%` }}></div>}
                                  {r.pct_sistemare > 0 && <div className="bg-amber-500" style={{ width: `${r.pct_sistemare}%` }}></div>}
                                  {r.pct_urgente > 0 && <div className="bg-red-500" style={{ width: `${r.pct_urgente}%` }}></div>}
                                </div>
                                <div className="text-[10px] text-muted-foreground font-mono flex gap-2">
                                  <span className="text-green-700">{r.counts["Buono"]} B</span>
                                  <span className="text-amber-700">{r.counts["Da sistemare"]} DS</span>
                                  <span className="text-red-700">{r.counts["Urgente"]} U</span>
                                </div>
                              </div>
                            ) : <span className="text-xs text-muted-foreground">Nessun controllo</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo/Modifica Controllo */}
      <Dialog open={dialog.open} onOpenChange={(open) => setDialog({ ...dialog, open })}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{dialog.editing ? t("controls.editControl") : t("controls.newControl")}</DialogTitle>
            <DialogDescription>{t("controls.registerVisitResult")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Appartamento *</Label>
              <SearchableSelect
                options={appartamenti.map(a => ({
                  value: a.id,
                  label: `${a.nome}${a.indirizzo ? ` — ${a.indirizzo}` : ""}`
                }))}
                value={form.appartamento_id}
                onValueChange={v => setForm({ ...form, appartamento_id: v })}
                placeholder="Seleziona appartamento..."
                searchPlaceholder="Cerca appartamento..."
                testId="select-appartamento"
              />
            </div>

            {/* Storico controlli precedenti */}
            {storicoAppartamento.length > 0 && (
              <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3" data-testid="storico-controlli">
                <div className="flex items-center gap-2 mb-2">
                  <History className="h-4 w-4 text-blue-700" />
                  <p className="text-sm font-medium text-blue-800">
                    Storico controlli precedenti ({storicoAppartamento.length})
                  </p>
                </div>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {storicoAppartamento.slice(0, 8).map(s => (
                    <div key={s.id} className="flex items-center justify-between text-xs bg-white border border-blue-100 rounded px-2 py-1.5">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="font-mono">{formatDate(s.data_visita)}</span>
                        {renderStatoBadge(s.stato)}
                      </div>
                      {s.note && (
                        <span className="text-muted-foreground truncate ml-2 max-w-xs" title={s.note}>
                          {s.note}
                        </span>
                      )}
                    </div>
                  ))}
                  {storicoAppartamento.length > 8 && (
                    <p className="text-xs text-muted-foreground italic mt-1">
                      …e altri {storicoAppartamento.length - 8} controlli precedenti
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Visita *</Label>
                <Input
                  type="date"
                  value={form.data_visita}
                  onChange={e => setForm({ ...form, data_visita: e.target.value })}
                  data-testid="input-data-visita"
                />
              </div>
              <div>
                <Label>Stato *</Label>
                <Select value={form.stato} onValueChange={v => setForm({ ...form, stato: v })}>
                  <SelectTrigger data-testid="select-stato"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATI.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note / Dettagli visita</Label>
              <Textarea
                rows={4}
                placeholder="Descrivi le condizioni rilevate, eventuali interventi da pianificare, ecc."
                value={form.note}
                onChange={e => setForm({ ...form, note: e.target.value })}
                data-testid="input-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, editing: null })}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} data-testid="btn-save">
              {dialog.editing ? "Salva" : "Crea Controllo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
